import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { ErrorsList } from '../errors'
import { SelectWrapper } from '../select-wrapper'
import { CustomSelect } from '../custom-select'
import { useToast } from '../toast'
import { TemplateBodyPreview } from './template-body-preview'
import { TemplateBodyInput } from './template-body-input'
import { TemplateBindingsEditor } from './template-bindings-editor'
import { TemplateServerErrors, templateServerErrors, toFieldErrors } from './template-server-errors'
import { whatsappTemplateApi } from 'src/services/whatsapp/api'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'
import { WhatsappTemplateForm, TWhatsappTemplateForm } from 'src/services/whatsapp/forms'
import { useTemplateBindings } from 'src/services/whatsapp/use-template-bindings'
import {
  templateCategoryEnum,
  RecommendedTemplate,
  templateLanguageOptions,
  WhatsappTemplate,
} from 'src/services/whatsapp/models'
import { InfoHint } from 'src/components/info-hint'

interface CreateTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  onSuccess?: (template: WhatsappTemplate) => void
  // Seeds the form from an existing template's content when duplicating —
  // the name is deliberately left blank so the new row always gets its own.
  sourceTemplate?: WhatsappTemplate | null
  // Seeds the form from a server-recommended template — name included, since
  // the org doesn't have a row by that name yet.
  recommendedTemplate?: RecommendedTemplate | null
  // Unlocks the check-in survey link in the variable picker.
  hasReportingProperties?: boolean
}

const CreateTemplateModalContent: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
  sourceTemplate,
  recommendedTemplate,
  hasReportingProperties,
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { form, createFormFieldChangeHandler } = useTnForm<TWhatsappTemplateForm>()

  // Prefill from the source template when duplicating. The surrounding
  // FormProvider is keyed on the modal's open state, so this runs once per open.
  useEffect(() => {
    const seed = sourceTemplate ?? recommendedTemplate
    if (!seed) return
    if (recommendedTemplate) createFormFieldChangeHandler(form._name)(recommendedTemplate.name)
    createFormFieldChangeHandler(form.category)(seed.category)
    createFormFieldChangeHandler(form.language)(seed.language)
    createFormFieldChangeHandler(form.body)(seed.body)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Every `{{variable}}` in the body must be mapped to a source before the
  // template can be submitted.
  const bodyValue = form.body.value ?? ''
  const { variables, bindings, setSource, setField, allMapped, buildBindings } =
    useTemplateBindings(
      bodyValue,
      sourceTemplate?.variableBindings ?? recommendedTemplate?.variable_bindings,
    )

  // Labels are translated here (not in the enum) so copy stays in i18n.
  const categoryOptions = [
    { value: templateCategoryEnum.UTILITY, label: t('whatsappTemplates.categoryUtility') },
    { value: templateCategoryEnum.MARKETING, label: t('whatsappTemplates.categoryMarketing') },
    {
      value: templateCategoryEnum.AUTHENTICATION,
      label: t('whatsappTemplates.categoryAuthentication'),
    },
  ]
  const languageOptions = templateLanguageOptions.map((code) => ({
    value: code,
    label: t(`whatsappTemplates.language_${code}`),
  }))

  // Field errors from a rejected save, shown inline under each field.
  const [serverErrors, setServerErrors] = useState<TemplateServerErrors>({})

  const {
    mutate: createTemplate,
    isPending,
    variables: pendingSubmit,
  } = useMutation({
    mutationFn: (submit: boolean) =>
      whatsappTemplateApi.create({
        organization: organizationId,
        name: form._name.value ?? '',
        category: form.category.value ?? templateCategoryEnum.UTILITY,
        language: form.language.value ?? 'en',
        body: form.body.value ?? '',
        variableBindings: buildBindings(),
        submit,
      }),
    onMutate: () => setServerErrors({}),
    onSuccess: (template, submit) => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      toast({
        description: t(submit ? 'whatsappTemplates.createSuccess' : 'whatsappTemplates.draftSaved'),
        type: 'success',
      })
      onSuccess?.(template)
      onClose()
    },
    onError: (error) => {
      setServerErrors(templateServerErrors(error))
      toast({ description: t('whatsappTemplates.createError'), type: 'error' })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.validate()
    if (!form.isValid || !allMapped) return
    createTemplate(true)
  }

  // A draft only needs what identifies it: the body and bindings may be
  // incomplete until it is submitted.
  const handleSaveDraft = () => {
    form._name.validate()
    form.language.validate()
    if (!form._name.isValid || !form.language.isValid) return
    createTemplate(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        sourceTemplate ? t('whatsappTemplates.duplicateTitle') : t('whatsappTemplates.createTitle')
      }
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-template-form">
        <div>
          <Input
            label={t('whatsappTemplates.formNameLabel')}
            placeholder={form._name.placeholder}
            value={form._name.value ?? ''}
            maxLength={128}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
            data-testid="template-name"
            autoFocus
          />
          <ErrorsList errors={form._name.errors} />
          <ErrorsList errors={toFieldErrors(serverErrors.name)} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <SelectWrapper
              label={t('whatsappTemplates.formCategoryLabel')}
              onBlur={() => form.category.validate()}
            >
              <CustomSelect
                options={categoryOptions}
                values={categoryOptions.filter((o) => o.value === form.category.value)}
                onChange={(values) => createFormFieldChangeHandler(form.category)(values[0]?.value)}
              />
            </SelectWrapper>
            <ErrorsList errors={form.category.errors} />
          </div>

          <div>
            <SelectWrapper
              label={t('whatsappTemplates.formLanguageLabel')}
              onBlur={() => form.language.validate()}
            >
              <CustomSelect
                options={languageOptions}
                values={languageOptions.filter((o) => o.value === form.language.value)}
                onChange={(values) => createFormFieldChangeHandler(form.language)(values[0]?.value)}
              />
            </SelectWrapper>
            <ErrorsList errors={form.language.errors} />
            <ErrorsList errors={toFieldErrors(serverErrors.language)} />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('whatsappTemplates.formBodyLabel')}
            <InfoHint text={t('whatsappTemplates.variablesHint')} className="ml-1.5" />
          </label>
          <TemplateBodyInput
            value={form.body.value ?? ''}
            onChange={(value) => createFormFieldChangeHandler(form.body)(value)}
            onBlur={() => form.body.validate()}
            placeholder={form.body.placeholder}
            hasReportingProperties={hasReportingProperties}
            data-testid="template-body"
          />
          <ErrorsList errors={form.body.errors} />
          <ErrorsList errors={toFieldErrors(serverErrors.body)} />
        </div>

        {form.body.value ? (
          <div className="rounded-md border border-border bg-surface-hover p-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-text-placeholder">
              {t('whatsappTemplates.previewLabel')}
            </p>
            <TemplateBodyPreview body={form.body.value} />
          </div>
        ) : null}

        <TemplateBindingsEditor
          variables={variables}
          bindings={bindings}
          onSourceChange={setSource}
          onFieldChange={setField}
        />
        <ErrorsList errors={toFieldErrors(serverErrors.variableBindings)} />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={isPending}
            onClick={handleSaveDraft}
            data-testid="save-draft-template"
          >
            {isPending && !pendingSubmit
              ? t('whatsappTemplates.savingDraft')
              : t('whatsappTemplates.saveDraft')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isPending || !allMapped}
            data-testid="submit-template"
          >
            {isPending && pendingSubmit
              ? t('whatsappTemplates.submitting')
              : t('whatsappTemplates.submitForApproval')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = (props) => {
  // Reset the form each time the modal opens (or switches duplication source)
  // by keying the provider on open state + source template id.
  return (
    <FormProvider
      formClass={WhatsappTemplateForm}
      key={
        props.isOpen
          ? `open-${
              props.sourceTemplate?.id ??
              (props.recommendedTemplate
                ? `${props.recommendedTemplate.name}-${props.recommendedTemplate.language}`
                : 'blank')
            }`
          : 'closed'
      }
    >
      <CreateTemplateModalContent {...props} />
    </FormProvider>
  )
}
